import type { Business } from '@/modules/dashboard/dashboardTypes';
import {
	Google,
	Instagram,
	Messenger,
	Trash,
	Website,
	Yelp
} from '@/resources/assets/images';
import { sendAnalytics } from '@/services/analyticsService';
import encodeUrl from '@/utils/encodeUrl';
import { ComponentProps, useCallback, useMemo } from 'react';
import Twitter from '@/assets/images/twitter-logo.png';
import Youtube from '@/assets/images/youtube-transparant.png';
import Pinterest from '@/assets/images/pinterest-transparant.png';
import Snapchat from '@/assets/images/snapchat-transparant.png';
import Tiktok from '@/assets/images/tiktok-transparant.png';
import type { BusinessInfoTabProps } from '../../BusinessInfoTab';

type OnlinePresenceSessionProps = Pick<
	Business,
	| 'name'
	| '_id'
	| 'address'
	| 'website'
	| 'facebook'
	| 'yelp'
	| 'socials'
	| 'userAddedData'
> &
	Pick<BusinessInfoTabProps, 'openDeleteDataModal'> & {
		onSaveNote: (
			activeKey: keyof NonNullable<Business['contactType']>
		) => void;
	};

type OnlinePresenceWrapperProps = ComponentProps<'a'>;

const OnlinePresenceWrapper = ({
	href,
	children,
	...props
}: OnlinePresenceWrapperProps) => {
	return (
		<li className="w-full">
			<a
				{...props}
				href={href}
				target="_blank"
				rel="noopener noreferrer"
				className="inline-flex w-full items-center gap-3"
			>
				{children}
			</a>
		</li>
	);
};

export const OnlinePresenceSession = ({
	name,
	_id,
	address,
	website,
	facebook,
	yelp,
	socials,
	userAddedData,
	onSaveNote,
	openDeleteDataModal
}: OnlinePresenceSessionProps) => {
	const handleEncodeUrl = useCallback((url: string) => {
		return encodeUrl(
			url.startsWith('http://') || url.startsWith('https://')
				? url
				: `http://${url}`
		);
	}, []);

	const mainWebsiteEncodeUrl = useMemo(() => {
		if (!website || website === 'false') return undefined;

		return handleEncodeUrl(website);
	}, [handleEncodeUrl, website]);

	const googleSearchEncodeUrl = useMemo(() => {
		if (!name || !address?.city || !address?.state) return undefined;

		const term = `${name} ${address?.city} ${address?.state}`;
		const parsedTerm = term.replace('&', '%26');

		return handleEncodeUrl(`https://google.com/search?q=${parsedTerm}`);
	}, [name, address?.city, address?.state, handleEncodeUrl]);

	const instagramEncodeUrl = useMemo(() => {
		if (!address?.city) return undefined;

		if (socials?.instagram) return socials?.instagram;

		const encodedCity = encodeURIComponent(address?.city || '');
		const encodedName = encodeURIComponent(name || '');

		return `https://www.google.com/search?q=${encodedCity}+${encodedName}+instagram`;
	}, [address?.city, socials?.instagram, name]);

	const userCustomDataEncodedUrls = useMemo(() => {
		if (!userAddedData || userAddedData?.length === 0) return [];

		const filteredUserCustomData = userAddedData.filter(
			({ type, data }) => {
				return type === 'web-link' && data && data !== 'false';
			}
		);

		const encodedUrlData = filteredUserCustomData.map(data => ({
			...data,
			encodedUrl: handleEncodeUrl(data.data)
		}));

		return encodedUrlData;
	}, [handleEncodeUrl, userAddedData]);

	const handleLinkAnalytics = (type: string) => {
		sendAnalytics({
			type: 'track',
			eventName: `User contacted a business via their: ${type}`,
			properties: {
				businessName: name,
				businessId: _id,
				businessAddress: address
			}
		});
	};

	return (
		<div className="flex w-full flex-col items-start gap-2">
			<strong className="flex items-center font-semibold text-dm-mid-grey">
				Online Presence
			</strong>

			<ul className="grid w-full grid-cols-2 gap-2">
				{googleSearchEncodeUrl && (
					<OnlinePresenceWrapper
						href={googleSearchEncodeUrl}
						onClick={() => {
							handleLinkAnalytics('GOOGLE');
							onSaveNote('google');
						}}
					>
						<Google className="aspect-square h-6 w-6 object-fill" />

						<span className="inline-block w-full overflow-hidden text-ellipsis whitespace-nowrap align-top text-lg font-semibold text-dm-white">
							Google Search
						</span>
					</OnlinePresenceWrapper>
				)}

				{mainWebsiteEncodeUrl && (
					<OnlinePresenceWrapper
						href={mainWebsiteEncodeUrl}
						onClick={() => {
							handleLinkAnalytics('WEBSITE');
							onSaveNote('website');
						}}
					>
						<Website className="inline-block aspect-square w-6 object-fill" />
						<span className="inline-block w-full overflow-hidden text-ellipsis whitespace-nowrap align-top text-lg font-semibold text-dm-white">
							Website
						</span>
					</OnlinePresenceWrapper>
				)}

				{yelp?.url && (
					<OnlinePresenceWrapper
						href={yelp?.url}
						onClick={() => {
							handleLinkAnalytics('YELP');
							onSaveNote('yelp');
						}}
					>
						<Yelp className="inline-block aspect-square w-6 object-fill" />
						<span className="inline-block w-full overflow-hidden text-ellipsis whitespace-nowrap align-top text-lg font-semibold text-dm-white">
							Yelp
						</span>
					</OnlinePresenceWrapper>
				)}

				{facebook?.url && (
					<OnlinePresenceWrapper
						href={facebook?.url}
						onClick={() => {
							handleLinkAnalytics('FACEBOOKWEBSITE');
							onSaveNote('facebook');
						}}
					>
						<Messenger className="inline-block aspect-square w-6 object-fill" />
						<span className="inline-block w-full overflow-hidden text-ellipsis whitespace-nowrap align-top text-lg font-semibold text-dm-white">
							Facebook
						</span>
					</OnlinePresenceWrapper>
				)}

				{instagramEncodeUrl && (
					<OnlinePresenceWrapper
						href={instagramEncodeUrl}
						onClick={() => {
							handleLinkAnalytics('INSTAGRAM');
							onSaveNote('instagram');
						}}
					>
						<Instagram className="inline-block aspect-square w-6 object-fill" />
						<span className="inline-block w-full overflow-hidden text-ellipsis whitespace-nowrap align-top text-lg font-semibold text-dm-white">
							{socials?.instagram
								? 'Instagram'
								: 'Find on Instagram'}
						</span>
					</OnlinePresenceWrapper>
				)}

				{socials?.twitter && (
					<OnlinePresenceWrapper
						href={socials?.twitter}
						onClick={() => {
							handleLinkAnalytics('TWITTER');
							onSaveNote('twitter');
						}}
					>
						<img
							src={Twitter}
							className="aspect-square h-5 w-5 object-fill"
							alt="twitter icon"
						/>
						<span className="inline-block w-full overflow-hidden text-ellipsis whitespace-nowrap align-top text-lg font-semibold text-dm-white">
							Find on Twitter
						</span>
					</OnlinePresenceWrapper>
				)}

				{socials?.tiktok && (
					<OnlinePresenceWrapper
						href={socials?.tiktok}
						onClick={() => {
							handleLinkAnalytics('TIKTOK');
							onSaveNote('tiktok');
						}}
					>
						<img
							src={Tiktok}
							className="inline-block aspect-square w-6 object-fill"
							alt="tiktok icon"
						/>
						<span className="inline-block w-full overflow-hidden text-ellipsis whitespace-nowrap align-top text-lg font-semibold text-dm-white">
							Find on TikTok
						</span>
					</OnlinePresenceWrapper>
				)}

				{socials?.snapchat && (
					<OnlinePresenceWrapper
						href={socials?.snapchat}
						onClick={() => {
							handleLinkAnalytics('SNAPCHAT');
							onSaveNote('snapchat');
						}}
					>
						<img
							src={Snapchat}
							className="inline-block aspect-square w-6 object-fill"
							alt="snapchat icon"
						/>
						<span className="inline-block w-full overflow-hidden text-ellipsis whitespace-nowrap align-top text-lg font-semibold text-dm-white">
							Find on Snapchat
						</span>
					</OnlinePresenceWrapper>
				)}

				{socials?.youtube && (
					<OnlinePresenceWrapper
						href={socials?.youtube}
						onClick={() => {
							handleLinkAnalytics('YOUTUBE');
							onSaveNote('youtube');
						}}
					>
						<img
							src={Youtube}
							className="inline-block aspect-square w-6 object-fill"
							alt="youtube icon"
						/>
						<span className="inline-block w-full overflow-hidden text-ellipsis whitespace-nowrap align-top text-lg font-semibold text-dm-white">
							Find on Youtube
						</span>
					</OnlinePresenceWrapper>
				)}

				{socials?.pinterest && (
					<OnlinePresenceWrapper
						href={socials?.pinterest}
						onClick={() => {
							handleLinkAnalytics('PINTEREST');
							onSaveNote('pinterest');
						}}
					>
						<img
							src={Pinterest}
							className="inline-block aspect-square w-6 object-fill"
							alt="pinterest icon"
						/>
						<span className="inline-block w-full overflow-hidden text-ellipsis whitespace-nowrap align-top text-lg font-semibold text-dm-white">
							Find on Pinterest
						</span>
					</OnlinePresenceWrapper>
				)}

				{userCustomDataEncodedUrls?.map((data, ind) => {
					if (data.type !== 'web-link') return null;

					return (
						<li
							className="flex w-full items-center justify-between"
							key={data.data}
						>
							<a
								href={data.encodedUrl}
								target="_blank"
								rel="noopener noreferrer"
								className="inline-flex w-full items-center gap-3"
								onClick={() => handleLinkAnalytics('WEBSITE')}
							>
								<Website className="inline-block aspect-square w-6 object-fill" />
								<span className="inline-block w-full overflow-hidden text-ellipsis whitespace-nowrap align-top text-lg font-semibold text-dm-white">
									{data.encodedUrl}
								</span>
							</a>

							<button
								type="button"
								onClick={() => {
									openDeleteDataModal(ind);
								}}
							>
								<Trash
									id="info-tab-web-trash"
									className="info-tab-site-trash cursor-pointer"
								/>
							</button>
						</li>
					);
				})}
			</ul>
		</div>
	);
};
